import React, { useState, useEffect } from 'react';
import { Minimize2, Play, Square, CheckCircle, Folder, FolderOpen, Target, Building2, Edit, Save, X, ClipboardList, Coffee } from 'lucide-react';
import { Task, TimeEntry, Goal, Matter, Project, Client } from '../../types';
import { db } from '../../lib/db';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTimer } from '../../contexts/TimerContext';

interface TimeEntryModalProps {
  task: Task;
  goal?: Goal;
  matter?: Matter;
  project?: Project;
  client?: Client;
  dbUserId: string;
  onClose: () => void;
  onTimeEntrySaved?: (wasCompleted?: boolean) => void;
}

function TimeEntryModal({ task, goal, matter, project, client, dbUserId, onClose, onTimeEntrySaved }: TimeEntryModalProps) {
  const { timerState, startTimer: startGlobalTimer, stopTimer: stopGlobalTimer, updateNotes, clearTimer, elapsedTime, breakElapsedTime, startBreak, endBreak, setTimeEntryId, setUserId } = useTimer();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(null);
  const [isEditingSOP, setIsEditingSOP] = useState(false);
  const [editedSOP, setEditedSOP] = useState(task.sop || '');
  
  // Check if this modal is for the currently running timer
  const isCurrentTimer = timerState.isRunning && timerState.task?.id === task.id;
  const isRunning = isCurrentTimer;
  const notes = isCurrentTimer ? timerState.notes : '';
  const startTime = isCurrentTimer ? timerState.startTime : null;

  useEffect(() => {
    loadTimeEntries();
    loadEstimate();
    // Reset SOP editing state when task changes
    setIsEditingSOP(false);
    setEditedSOP(task.sop || '');
    // Automatically start the timer when modal opens if not already running
    if (!isCurrentTimer && !timerState.isRunning) {
      startTimer();
    }
  }, [task.id, isCurrentTimer, timerState.isRunning]);

  // Handle notes updates
  const handleNotesChange = (value: string) => {
    if (isCurrentTimer) {
      updateNotes(value);
    }
  };

  const loadTimeEntries = async () => {
    try {
      const entries = await db.timeEntries.findByTaskId(task.id, dbUserId);
      setTimeEntries(entries);
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  };

  const loadEstimate = async () => {
    try {
      const stats = await db.taskCompletionStatistics.getEstimate(
        task.title, 
        task.description, 
        dbUserId, 
        goal?.template_id
      );
      if (stats) {
        setEstimatedMinutes(stats.avg_minutes_per_completion);
      }
    } catch (error) {
      console.error('Error loading estimate:', error);
    }
  };

  const saveSOP = async () => {
    try {
      const newSOP = editedSOP.trim() || null;
      
      if (task.is_regular) {
        // For regular tasks, update all instances
        await db.tasks.updateRegularTaskSOP(task.id, newSOP, dbUserId);
        toast.success('SOP updated for all instances of this regular task');
      } else {
        // For non-regular tasks (though SOP shouldn't be shown for these)
        await db.tasks.update(task.id, dbUserId, {
          sop: newSOP
        });
        toast.success('SOP updated successfully');
      }
      
      // Update local task object
      task.sop = newSOP || undefined;
      
      setIsEditingSOP(false);
      
      if (onTimeEntrySaved) {
        onTimeEntrySaved();
      }
    } catch (error) {
      console.error('Error updating SOP:', error);
      toast.error('Failed to update SOP');
    }
  };

  const cancelSOPEdit = () => {
    setEditedSOP(task.sop || '');
    setIsEditingSOP(false);
  };

  const startTimer = async () => {
    const now = new Date();
    
    // Start the global timer with userId
    startGlobalTimer(task, goal, matter, project, client, dbUserId);

    // Update task start date if not set
    if (!task.start_date) {
      try {
        await db.tasks.update(task.id, dbUserId, {
          start_date: now,
          status: 'In Progress'
        });
        
        if (onTimeEntrySaved) {
          onTimeEntrySaved();
        }
      } catch (error) {
        console.error('Error updating task start date:', error);
      }
    }
  };

  const stopTimer = async () => {
    // Require notes before stopping
    if (!notes.trim()) {
      toast.error('Please add notes describing what you worked on before stopping the timer');
      return;
    }

    // Get timer info from global state
    const timerInfo = stopGlobalTimer();
    if (!timerInfo) return;
    
    try {
      // Check if the task still exists before creating time entry
      const taskExists = await db.tasks.findById(task.id, dbUserId);
      if (!taskExists) {
        toast.error('This task has been deleted. Timer has been stopped but time entry was not saved.');
        clearTimer();
        onClose();
        return;
      }

      const timeEntry = await db.timeEntries.create({
        task_id: task.id,
        user_id: dbUserId,
        start_time: timerInfo.startTime,
        end_time: timerInfo.endTime,
        notes: timerInfo.notes.trim(),
      });
      
      // Set the time entry ID in the timer context for break tracking
      setTimeEntryId(timeEntry.id);
      
      await loadTimeEntries();
      
      if (onTimeEntrySaved) {
        onTimeEntrySaved();
      }
      
      // Clear the timer completely to ensure it doesn't appear to continue running
      clearTimer();
      
      // Close the modal after successfully stopping the timer
      onClose();
    } catch (error) {
      console.error('Error creating time entry:', error);
      
      // Check if error is due to foreign key constraint
      if (error.message && error.message.includes('foreign key constraint')) {
        toast.error('This task has been deleted. Timer has been stopped but time entry was not saved.');
        clearTimer();
        onClose();
      } else {
        toast.error('Failed to save time entry');
      }
    }
  };

  const formatDuration = (start: Date | string, end: Date | string | null | undefined) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diff = endDate.getTime() - startDate.getTime();
    
    // Round up to the nearest minute
    const totalMinutes = Math.ceil(diff / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  const formatMinuteDuration = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    if (hours === 0) return `${minutes}m`;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const totalTime = timeEntries.reduce((total, entry) => {
    if (entry.end_time) {
      const start = new Date(entry.start_time);
      const end = new Date(entry.end_time);
      const diff = end.getTime() - start.getTime();
      // Round up each entry to the nearest minute
      return total + Math.ceil(diff / 60000) * 60000;
    }
    return total;
  }, 0);

  const totalMinutesRounded = Math.ceil(totalTime / 60000);
  const totalHours = Math.floor(totalMinutesRounded / 60);
  const totalMinutes = totalMinutesRounded % 60;

  const completeTask = async () => {
    // If timer is running, stop it first (which requires notes)
    if (isRunning) {
      if (!notes.trim()) {
        toast.error('Please add notes describing what you worked on before completing the task');
        return;
      }
      await stopTimer();
    }
    
    try {
      const now = new Date();
      
      // Calculate total time spent from all time entries
      const totalMinutes = await db.timeEntries.getTotalMinutesForTask(task.id);

      // Check if this is a regular task
      if (task.is_regular) {
        // For regular tasks, we DON'T update the status to Complete
        // Instead, we just notify the parent component to handle it properly
        if (onTimeEntrySaved) {
          onTimeEntrySaved(true);
        }
        toast.success('Regular task completed!');
      } else {
        // For non-regular tasks, update status normally with time_spent
        await db.tasks.update(task.id, dbUserId, {
          status: 'Complete',
          end_date: now,
          time_spent: totalMinutes,
          time_spent_minutes: totalMinutes
        });

        await db.taskCompletionStatistics.recordCompletion({
          userId: dbUserId,
          taskId: task.id,
          taskTitle: task.title,
          taskDescription: task.description,
          priority: task.priority,
          minutesSpent: totalMinutes
        });

        toast.success('Task marked as complete!');
        
        if (onTimeEntrySaved) {
          onTimeEntrySaved(true);
        }
      }
      
      // Clear the timer to ensure it doesn't continue running
      clearTimer();
      
      onClose();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };


  // Full view
  return (
    <>
      {/* Break Overlay */}
      {timerState.isOnBreak && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <Coffee className="w-16 h-16 text-amber-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">On Break</h2>
              <p className="text-gray-600 mb-6">Timer is paused while you take a break</p>
              
              <div className="mb-6">
                <div className="text-4xl font-mono font-bold text-amber-600">
                  {breakElapsedTime}
                </div>
                <p className="text-sm text-gray-500 mt-1">Break duration</p>
              </div>
              
              <button
                onClick={endBreak}
                className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
              >
                Break Over - Resume Timer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-hidden sm:rounded-lg sm:max-h-[80vh] rounded-none max-h-full h-full sm:h-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">Time Tracking: {task.title}</h2>
              {/* Hierarchy breadcrumb */}
              {(client || project || matter || goal) && (
                <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600">
                  {client && (
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-1 text-orange-500" />
                      <span className="text-orange-700 font-medium">{client.name}</span>
                    </div>
                  )}
                  {client && project && <span className="text-gray-400">›</span>}
                  {project && (
                    <div className="flex items-center">
                      <FolderOpen className="w-4 h-4 mr-1 text-purple-500" />
                      <span className="text-purple-700">{project.title}</span>
                    </div>
                  )}
                  {project && matter && <span className="text-gray-400">›</span>}
                  {matter && (
                    <div className="flex items-center">
                      <Folder className="w-4 h-4 mr-1 text-blue-500" />
                      <span className="text-blue-700">{matter.title}</span>
                    </div>
                  )}
                  {(project || matter) && goal && <span className="text-gray-400">›</span>}
                  {goal && (
                    <div className="flex items-center">
                      <Target className="w-4 h-4 mr-1 text-green-500" />
                      <span className="text-green-700">{goal.title}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                // If timer is running, just close the modal but keep timer running
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600"
              title={isRunning ? "Minimize (timer continues in background)" : "Close"}
            >
              <Minimize2 className="w-5 h-5" />
            </button>
          </div>
          {task.status !== 'Complete' && (
            <div className="mt-4">
              <button
                onClick={completeTask}
                disabled={isRunning && !notes.trim()}
                className={`w-full flex items-center justify-center px-4 py-2 rounded-md transition-colors ${
                  isRunning && !notes.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                title={isRunning && !notes.trim() ? 'Add notes before completing task' : ''}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete Task
              </button>
              {isRunning && !notes.trim() && (
                <p className="text-xs text-red-500 mt-1 text-center">
                  Add notes about your work before completing the task
                </p>
              )}
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 h-[calc(100vh-200px)] sm:h-[400px] flex flex-col">
          {/* Main Layout - Two Column with fixed height */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
            {/* Left Column - SOP (scrollable) and Timer (fixed at bottom) */}
            <div className="flex flex-col min-h-0">
              {/* SOP Section - Scrollable - Only show for regular/template tasks */}
              <div className="flex-1 min-h-0 mb-4">
                {task.is_regular ? (
                  (task.sop || isEditingSOP) ? (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                      <div className="flex items-center">
                        <ClipboardList className="w-4 h-4 text-blue-600 mr-2" />
                        <h3 className="text-sm font-medium text-blue-900">SOP</h3>
                      </div>
                      {!isEditingSOP ? (
                        <button
                          onClick={() => setIsEditingSOP(true)}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                          title="Edit SOP"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={saveSOP}
                            className="flex items-center text-xs text-green-600 hover:text-green-800 transition-colors"
                            title="Save SOP"
                          >
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </button>
                          <button
                            onClick={cancelSOPEdit}
                            className="flex items-center text-xs text-gray-600 hover:text-gray-800 transition-colors"
                            title="Cancel editing"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {!isEditingSOP ? (
                      <div className="text-sm text-blue-800 whitespace-pre-wrap overflow-y-auto flex-1">
                        {task.sop}
                      </div>
                    ) : (
                      <textarea
                        value={editedSOP}
                        onChange={(e) => setEditedSOP(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none flex-1"
                        placeholder="Enter step-by-step instructions for completing this task...&#10;&#10;Example:&#10;1. First step...&#10;2. Second step...&#10;3. Third step..."
                      />
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-full flex items-center justify-center">
                    <div className="text-center">
                      <ClipboardList className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <span className="text-sm text-gray-600 block mb-2">No SOP defined</span>
                      <button
                        onClick={() => setIsEditingSOP(true)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <Edit className="w-3 h-3 inline mr-1" />
                        Add SOP
                      </button>
                    </div>
                  </div>
                )
                ) : null}
              </div>

              {/* Timer Controls - Fixed at bottom */}
              <div className="bg-gray-50 p-6 rounded-lg flex-shrink-0">
                <div className="text-center">
                  <div className="text-4xl font-mono font-bold mb-2">
                    {elapsedTime}
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    {estimatedMinutes && estimatedMinutes > 0 
                      ? `Estimated: ${formatMinuteDuration(estimatedMinutes)}`
                      : 'No Time Estimate'}
                  </div>
                  <div className="flex justify-center space-x-4">
                    {!isRunning ? (
                      <button
                        onClick={startTimer}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Timer
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={startBreak}
                          disabled={timerState.isOnBreak}
                          className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                            !timerState.isOnBreak
                              ? 'bg-amber-600 text-white hover:bg-amber-700' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          <Coffee className="w-4 h-4 mr-2" />
                          Break
                        </button>
                        <button
                          onClick={stopTimer}
                          disabled={!notes.trim()}
                          className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                            notes.trim() 
                              ? 'bg-red-600 text-white hover:bg-red-700' 
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                          title={!notes.trim() ? 'Add notes before stopping timer' : ''}
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop Timer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Complete History (scrollable) and Notes (fixed at bottom) */}
            <div className="flex flex-col min-h-0">
              {/* Complete History - Scrollable */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex-1 min-h-0 mb-4 flex flex-col">
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <h3 className="text-sm font-medium text-gray-700">Complete History</h3>
                  <div className="text-sm text-gray-600">
                    Total: {totalHours}h {totalMinutes}m
                  </div>
                </div>
                <div className="overflow-y-auto space-y-2 flex-1">
                  {timeEntries.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No time entries yet</p>
                  ) : (
                    timeEntries.slice().reverse().map((entry) => (
                      <div key={entry.id} className="bg-white border border-gray-200 rounded p-3">
                        <div className="flex justify-between items-start text-sm">
                          <div className="flex-1">
                            <div className="text-gray-600 font-medium">
                              {format(new Date(entry.start_time), 'MMM d, h:mm a')}
                              {entry.end_time && ` - ${format(new Date(entry.end_time), 'h:mm a')}`}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">
                              {formatDuration(entry.start_time, entry.end_time)}
                            </div>
                            {entry.notes && (
                              <div className="text-gray-800 text-xs mt-1">{entry.notes}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Notes Section - Fixed at bottom */}
              {isRunning && (
                <div className="flex-shrink-0">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-1">(required to stop timer)</span>
                  </label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md ${
                      notes.trim() ? 'border-gray-300' : 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    }`}
                    rows={4}
                    placeholder="Describe what you worked on (required)"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default TimeEntryModal;
